import React, { useState } from "react";
import ModalWrapper from "../../../app/common/modals/ModalWrapper";
import { Form, Formik } from "formik";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { Button, Checkbox, Grid, Label } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, openModal } from "../../../app/common/modals/modalReducer";
import {
  registerJobSeekerInFirebase,
  AUTH_FACEBOOK_PROVIDER,
} from "../../../app/firestore/firebaseService";
import {
  PRIVACY_POLICY_ROUTE,
  TERMS_AND_CONDITION_ROUTE,
} from "../../../app/api/routeConstants";
import { verifyAuth } from "../authActions";
import { SIGN_UP_JOB_SEEKER_VALIDATION_SCHEMA } from "../../../js/constants/validationSchemas";
import MyPasswordInput from "../../../app/common/form/MyPasswordInput";
import { SIGN_UP_MODAL } from "../../../app/common/modals/ModalManager";
import ReactGA from "react-ga4";
import TextLink from "../../../app/common/components/TextLink";
import { getCountryProperties } from "../../data/countries";
import { getMap } from "../../map/mapSelector";
import BackButton from "../../../app/common/components/BackButton";
import SocialLogin from "../SocialLogin";
import Divider from "../../../app/common/components/Divider";

export default function SignUpJobSeekerModal({ isShowBack = true }) {
  const dispatch = useDispatch();

  const { isCookieConsent, responsive } = useSelector((state) => state.app);
  const { coverage } = useSelector((state) => getMap(state));

  const country = getCountryProperties(coverage.countryCode);

  const [isReceiveMarketing, setIsReceiveMarketing] = useState(false);

  return (
    <ModalWrapper
      style={{ minHeight: 400, width: !responsive.isMobile ? 420 : "90%" }}
      modalClassName={"modal-ux-wrapper"}
      contentClassName={"sign-up-modal-content"}
      showAnimation={"fade"}
      showDuration={450}
      hideAnimation={"fade"}
      hideDuration={160}
    >
      <Grid
        stackable
        className={
          !responsive.isMobile
            ? "sign-up-user-container"
            : "sign-up-container-mobile"
        }
      >
        <Grid.Row style={{ padding: "8px 0 0 0" }}>
          {isShowBack && (
            <BackButton
              action={() => dispatch(openModal({ modalType: SIGN_UP_MODAL }))}
            />
          )}
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <div style={{ marginBottom: 20 }}>
              <h2>Create a Job Seeker Account</h2>
            </div>
            <SocialLogin buttonText={"Continue with Google"} />
            <SocialLogin
              buttonText={"Continue with Facebook"}
              provider={AUTH_FACEBOOK_PROVIDER}
              margin={"10px 0 0 0"}
            />
            <Divider text={"or"} padding={"8px 0 4px"} />
            <Formik
              initialValues={{
                displayName: "",
                email: "",
                password: "",
              }}
              validationSchema={SIGN_UP_JOB_SEEKER_VALIDATION_SCHEMA}
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                try {
                  await registerJobSeekerInFirebase({
                    values: values,
                    countryCode: country.countryCode,
                    isReceiveMarketing: isReceiveMarketing,
                  });
                  if (isCookieConsent) {
                    ReactGA.event({
                      category: "Metrics",
                      action: "User sign-up",
                      label: "Job Seeker",
                    });
                  }
                  setSubmitting(false);
                  dispatch(closeModal());
                  dispatch(verifyAuth());
                } catch (error) {
                  setErrors({ auth: error.message });
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, isValid, dirty, errors, setFieldTouched }) => (
                <Form className="ui form">
                  <div className="sign-up-field-input-container">
                    <MyTextInput
                      className="form-input-field"
                      name="firstName"
                      placeholder="First name*"
                      errorClasses="error-message absolute-position"
                      autoComplete="off"
                    />
                  </div>
                  <div className="sign-up-field-input-container">
                    <MyTextInput
                      className="form-input-field"
                      name="lastName"
                      placeholder="Last name*"
                      errorClasses="error-message absolute-position"
                      autoComplete="off"
                    />
                  </div>
                  <div className="sign-up-field-input-container">
                    <MyTextInput
                      className="form-input-field"
                      name="email"
                      placeholder="Email address*"
                      errorClasses="error-message absolute-position"
                      autoComplete="off"
                    />
                  </div>
                  <div className="sign-up-field-input-container">
                    <MyPasswordInput
                      name="password"
                      placeholder="Password*"
                      errorClasses="error-message absolute-position"
                      autoComplete="off"
                    />
                  </div>
                  <Checkbox
                    style={{ margin: "20px 10px 10px 0" }}
                    onChange={(e, { checked }) => {
                      setFieldTouched("password", true, true);
                      setIsReceiveMarketing(checked);
                    }}
                    label={{
                      children: (
                        <span className="body-text">
                          I would like to receive updates about new features and
                          events.
                        </span>
                      ),
                    }}
                  />
                  <div className="body-text">
                    <span>By creating an account you agree to the </span>
                    <TextLink
                      text={"Terms & Conditions"}
                      to={TERMS_AND_CONDITION_ROUTE}
                      fontSize={15}
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                    <span> and </span>
                    <TextLink
                      text={"Privacy Policy"}
                      to={PRIVACY_POLICY_ROUTE}
                      fontSize={15}
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  </div>
                  <Button
                    className="overwrite-button cta-long-button"
                    type={"submit"}
                    loading={isSubmitting}
                    disabled={!isValid || !dirty || isSubmitting}
                    style={{ margin: "20px 0 0 0" }}
                    content={"Sign up"}
                  />
                  {errors.auth && (
                    <div style={{ paddingTop: 10 }}>
                      <Label
                        basic
                        color="grey"
                        style={{ marginBottom: 10 }}
                        content={"Could not sign up with this email"}
                      />
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div className="sign-up-strapline-container">
        <div
          className={
            !responsive.isMobile
              ? "sign-up-strapline-text"
              : "sign-up-strapline-text-mobile"
          }
        >
          <span className={"sign-up-strapline-underline"}>hello new job,</span>
          <span> hello new hire</span>
          <span className="accent-colour">.</span>
        </div>
      </div>
    </ModalWrapper>
  );
}
